import * as React from 'react';
import {StyleSheet, View, Text, TextInput} from 'react-native';
import {CheckBox} from 'react-native-elements';
import {withGlobalize, WithGlobalizeProps} from 'react-native-globalize';
import RadioForm, {
  RadioButton,
  RadioButtonInput,
  RadioButtonLabel,
} from 'react-native-simple-radio-button';
import type {ProductModifierType, ProductModifierValue} from 'src/types/Menu';
import Styles from '../../Styles';
import Util from 'src/Util';
import AccountStore from 'src/stores/AccountStore';
import Localized from 'src/constants/AppStrings';
type ProductModifierProps = WithGlobalizeProps & {
  productModifier: ProductModifierType;
  selectionChanged: (modifierId: string, selections: Array<string>) => void;
  selectedValues?: Array<string>;
};
type ProductModifierState = {
  values: Array<string>;
  radio: boolean;
  required: boolean;
  freeText: boolean;
};
const FREE_TEXT_MAX_LENGTH = 50;
export const ModifierTypes = {
  FreeText: 4,
  Upsell: 5,
};

class ProductModifier extends React.Component<
  ProductModifierProps,
  ProductModifierState
> {
  constructor(props: ProductModifierProps) {
    super(props);
    const radio =
      props.productModifier.maxsel === 1 &&
      props.productModifier.type !== ModifierTypes.Upsell;
    let values = props.productModifier.vals
      .filter((value) => value.isdefault === 'Y')
      .map((value) => value.id);

    if (this.props.selectedValues) {
      values = this.props.selectedValues;
    }

    if (!values) {
      values = [];
    }

    this.state = {
      values,
      radio,
      required: props.productModifier.forced === 'Y',
      freeText: props.productModifier.type === ModifierTypes.FreeText,
    };

    if (values.length > 0) {
      props.selectionChanged(props.productModifier.id, values);
    }

    this.onPress = this.onPress.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  onPress(value: string) {
    let values = [];

    if (this.state.radio) {
      if (!this.state.values.includes(value)) {
        values = [value];
      }
    } else {
      if (this.state.values.includes(value)) {
        values = this.state.values.filter((item) => item !== value);
      } else if (
        this.state.values.length < this.props.productModifier.maxsel ||
        this.props.productModifier.type === ModifierTypes.Upsell
      ) {
        values = [...this.state.values, value];
      } else {
        // values haven't changed
        values = [...this.state.values];
      }
    }

    this.setState(
      {
        values,
      },
      () =>
        this.props.selectionChanged(
          this.props.productModifier.id,
          this.state.values,
        ),
    );
  }

  renderCheckBoxes() {
    return this.props.productModifier.vals.map((value) => {
      const title = this.getTitle(value);
      return (
        <CheckBox
          title={title}
          checked={this.state.values.includes(value.id)}
          key={value.id}
          size={Styles.Fonts.f2}
          textStyle={[styles.optionText, styles.checkBoxText]}
          containerStyle={styles.radioButtonWrapper}
          onPress={() => this.onPress(value.id)}
          checkedColor={Styles.primaryColor}
          uncheckedColor={Styles.auxColor}
        />
      );
    });
  }

  getTitle(value: ProductModifierValue) {
    let title = value.name;

    if (value.price) {
      const price = parseFloat(value.price);

      if (price > 0) {
        title = `${title} (+${Util.formatCurrency(
          this.props,
          price,
          AccountStore.getCurrency(),
        )})`;
      }
    }

    return title;
  }

  renderRadioButtons() {
    const buttons = this.props.productModifier.vals.map((value) => {
      const title = this.getTitle(value);
      return {
        label: title,
        value: value.id,
      };
    });
    return (
      <RadioForm>
        {buttons.map((obj, i) => {
          const isSelected = this.state.values.includes(obj.value);
          return (
            <RadioButton key={i} style={styles.radioButtonWrapper}>
              <RadioButtonInput
                obj={obj}
                index={i}
                buttonInnerColor={Styles.primaryColor}
                buttonOuterColor={
                  isSelected ? Styles.primaryColor : Styles.auxColor
                }
                buttonSize={Styles.Fonts.f0}
                buttonOuterSize={Styles.Fonts.f1}
                borderWidth={1}
                isSelected={isSelected}
                onPress={this.onPress}
                accessibilityLabel={obj.label}
                nativeID={obj.label}
              />
              <RadioButtonLabel
                obj={obj}
                index={i}
                labelStyle={[styles.optionText, styles.radioText]}
                onPress={this.onPress}
                accessibilityLabel={`${obj.label} Label`}
                nativeID={`${obj.label} Label`}
              />
            </RadioButton>
          );
        })}
      </RadioForm>
    );
  }

  onChange(value: string) {
    const values = [value];
    this.setState(
      {
        values,
      },
      () =>
        this.props.selectionChanged(
          this.props.productModifier.id,
          this.state.values,
        ),
    );
  }

  renderFreeTextInput() {
    return (
      <TextInput
        style={styles.input}
        value={this.state.values?.[0]}
        multiline={true}
        accessibilityLabel={this.props.productModifier.name}
        numberOfLines={3}
        onChangeText={this.onChange}
        maxLength={FREE_TEXT_MAX_LENGTH}
      />
    );
  }

  render() {
    const {
      productModifier: {type, maxsel, name},
    } = this.props;
    const {required, freeText, values} = this.state;
    let content: React.ReactNode = this.renderCheckBoxes();

    if (type === ModifierTypes.FreeText) {
      content = this.renderFreeTextInput();
    } else if (maxsel === 1 && type !== ModifierTypes.Upsell) {
      content = this.renderRadioButtons();
    }

    return (
      <View>
        <View style={styles.titleRow}>
          <Text accessibilityLabel={'Product Name'} style={styles.titleText}>
            {name}
          </Text>
          {required && (
            <Text style={styles.requiredText}>{Localized.Labels.required}</Text>
          )}
          {freeText && (
            <Text style={styles.freeText}>
              {this.getCounterText(Localized, values?.[0])}
            </Text>
          )}
        </View>
        <View style={styles.options}>{content}</View>
      </View>
    );
  }

  getCounterText(strings, value) {
    return `${FREE_TEXT_MAX_LENGTH - (value?.length || 0)} ${
      strings.Labels.characters_left
    }`;
  }
}

const styles = StyleSheet.create({
  titleRow: {
    marginTop: Styles.Spacing.m2,
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  titleText: {
    fontWeight: '600',
    fontSize: Styles.Fonts.f1,
  },
  optionText: {
    fontSize: Styles.Fonts.f0,
    marginLeft: Styles.Spacing.m2,
    fontWeight: null,
    color: Styles.gray20,
    marginTop: -Styles.Spacing.m1 / 2,
  },
  radioText: {
    marginLeft: Styles.Spacing.m2,
  },
  options: {
    paddingTop: Styles.Spacing.m2,
  },
  checkBoxText: {
    marginLeft: Styles.Spacing.m1,
  },
  radioButtonWrapper: {
    borderBottomWidth: 1,
    borderColor: Styles.bgColor,
    paddingVertical: Styles.Spacing.m1,
    backgroundColor: Styles.white,
    borderWidth: 0,
    marginLeft: Styles.Spacing.m2,
    padding: null,
    margin: null,
    borderRadius: null,
  },
  requiredText: {
    fontSize: Styles.Fonts.f1,
    color: Styles.dangerColor,
    fontStyle: 'italic',
  },
  freeText: {
    fontSize: Styles.Fonts.f0,
    color: Styles.gray40,
  },
  input: {
    borderRadius: Styles.Spacing.m1,
    borderColor: Styles.gray,
    borderWidth: 1,
    padding: Styles.Spacing.m1,
  },
});
export default withGlobalize(ProductModifier);
